import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon, Building2Icon, LoaderCircleIcon, LifeBuoyIcon, MapPinIcon, PartyPopperIcon, SearchIcon } from 'lucide-react';
import { BrandMark } from '../components/BrandMark';
import { Confetti } from '../components/Confetti';
import { Field } from '../components/Field';
import { TermsStage } from '../components/signup/TermsStage';
import { PlanSummaryBar } from '../components/signup/PlanSummaryBar';
import { PlanStage } from '../components/signup/PlanStage';
import { SignupAside } from '../components/signup/SignupAside';
import { companies, Company } from '../data/demoData';
import { findDiscount, formatPrice, pricingPlans, SelectedPlan, toSelectedPlan } from '../data/pricingPlans';
import { useScreenInit } from '../useScreenInit';
type JourneyStep = 'plan' | 'contact' | 'company' | 'sole-trader' | 'terms' | 'complete';
export type ContactForm = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
};
type OnboardingPageProps = {
  initialContact?: ContactForm;
};
export function OnboardingPage({
  initialContact
}: OnboardingPageProps) {
  const screenInit = useScreenInit() as {
    step?: JourneyStep;
    discountApplied?: boolean;
  };
  const urlParams = typeof window === 'undefined' ? null : new URLSearchParams(window.location.search);
  const urlPlan = urlParams?.get('plan')?.toLowerCase() || null;
  const urlCode = urlParams?.get('code') || '';
  /**
   * Arriving from a page that already sold a specific plan (the FORE Business
   * offer, or any /signup?plan= link) means the tier is already decided, so the
   * plan step is skipped entirely for that visitor.
   */
  const planPreset = Boolean(screenInit.discountApplied) || Boolean(pricingPlans.find(plan => plan.id === urlPlan));
  const [step, setStep] = useState<JourneyStep>(() => {
    const initialStep = screenInit.step;
    if (initialStep === 'plan' || initialStep === 'contact' || initialStep === 'company' || initialStep === 'sole-trader' || initialStep === 'terms' || initialStep === 'complete') {
      return initialStep;
    }
    if (initialContact) return 'company';
    return planPreset ? 'contact' : 'plan';
  });
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(() => {
    if (screenInit.discountApplied) return 'advanced';
    return pricingPlans.find(plan => plan.id === urlPlan)?.id || (screenInit.step && screenInit.step !== 'plan' ? 'advanced' : null);
  });
  const [planError, setPlanError] = useState(false);
  const discount = screenInit.discountApplied ? findDiscount('FORE') : findDiscount(urlCode);
  const [contact, setContact] = useState<ContactForm>(initialContact || {
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });
  const [contactErrors, setContactErrors] = useState<Partial<Record<keyof ContactForm, string>>>({});
  const [companyQuery, setCompanyQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [soleBusiness, setSoleBusiness] = useState('');
  const [soleAddress, setSoleAddress] = useState('');
  const [termsBackStep, setTermsBackStep] = useState<'company' | 'sole-trader'>('company');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [termsError, setTermsError] = useState(false);
  const selectedPlan: SelectedPlan | null = (() => {
    const plan = pricingPlans.find(entry => entry.id === selectedPlanId);
    return plan ? toSelectedPlan(plan, discount) : null;
  })();
  useEffect(() => {
    if (companyQuery.trim().length < 2 || selectedCompany) {
      setSearching(false);
      return;
    }
    setSearching(true);
    const timer = window.setTimeout(() => setSearching(false), 280);
    return () => window.clearTimeout(timer);
  }, [companyQuery, selectedCompany]);
  const updateContact = (field: keyof ContactForm, value: string) => {
    setContact(current => ({
      ...current,
      [field]: value
    }));
    setContactErrors(current => ({
      ...current,
      [field]: undefined
    }));
  };
  const visibleResults = companyQuery.trim().length >= 2 && !selectedCompany ? companies.filter(company => company.name.toLowerCase().includes(companyQuery.toLowerCase())).slice(0, 3) : [];
  function continueContact() {
    const nextContact = {
      firstName: contact.firstName.trim(),
      lastName: contact.lastName.trim(),
      email: contact.email.trim(),
      phone: contact.phone.trim()
    };
    const errors: Partial<Record<keyof ContactForm, string>> = {};
    if (!nextContact.firstName) errors.firstName = 'Enter your first name.';
    if (!nextContact.lastName) errors.lastName = 'Enter your last name.';
    if (!nextContact.email) {
      errors.email = 'Enter your email address.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(nextContact.email)) {
      errors.email = 'Enter a valid email address.';
    }
    if (!nextContact.phone) errors.phone = 'Enter your phone number.';
    if (Object.keys(errors).length) {
      setContactErrors(errors);
      return;
    }
    setContact(nextContact);
    setContactErrors({});
    setStep('company');
  }
  function goToTerms(from: 'company' | 'sole-trader') {
    setTermsBackStep(from);
    setTermsError(false);
    setStep('terms');
  }
  function submitTerms() {
    if (!acceptedTerms) {
      setTermsError(true);
      return;
    }
    setTermsError(false);
    setStep('complete');
  }
  return <div className="min-h-screen w-full bg-white text-[#041F60] lg:grid lg:grid-cols-[1fr_minmax(24rem,40%)]" data-id="element-727" data-mp="dq4r6qwxte3s">
      <div className="flex min-h-screen flex-col" data-id="element-728" data-mp="2tgr0zjhnutoi">
        <main className="mx-auto w-full max-w-xl flex-1 px-5 py-8 sm:px-8 sm:py-12" data-id="element-729" data-mp="2tk54ww5z3ay2">
          <a href="/" aria-label="Credit Manager home" className="mb-10 inline-block rounded-lg outline-none focus:ring-2 focus:ring-[#041F60] focus:ring-offset-4" data-id="element-730" data-mp="2yesja279ioyx">
            <BrandMark data-id="element-731" data-mp="z3wo5pgqabr3" />
          </a>

          {step !== 'complete' && <ProgressRail step={step} includePlanStep={!planPreset} data-id="element-732" data-mp="u90uylzr9ice" />}

          <div data-id="element-733" data-mp="q8gyzlqx4kru">
            {step !== 'complete' && step !== 'plan' && <PlanSummaryBar plan={selectedPlan} onChangePlan={planPreset ? undefined : () => setStep('plan')} data-id="element-734" data-mp="lafeqxhrafes" />}

            {step === 'plan' && <section className="mt-9" aria-labelledby="plan-title" data-id="element-735" data-mp="bd0zh17kjwo2">
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#5B5299]" data-id="element-736" data-mp="7anvah8h06te">
                  Choose your plan
                </p>
                <h1 id="plan-title" className="mt-3 text-3xl font-bold tracking-tight text-[#041F60]" data-id="element-737" data-mp="25ph3dvcw6ypr">
                  Which plan suits you?
                </h1>
                <p className="mt-3 text-[15px] leading-6 text-stone-600" data-id="element-738" data-mp="3jjxw192zipq2">
                  Every plan starts with a free trial. You won’t be charged
                  until it ends.
                </p>
                <PlanStage selectedPlanId={selectedPlanId} discount={discount} showError={planError} onSelect={planId => {
              setSelectedPlanId(planId);
              setPlanError(false);
            }} onContinue={() => {
              if (!selectedPlanId) {
                setPlanError(true);
                return;
              }
              setStep('contact');
            }} data-id="element-739" data-mp="3tdy5to97oj54" />
              </section>}

          {step === 'contact' && <section className="mt-9" aria-labelledby="contact-title" data-id="element-740" data-mp="twgfxbfqy5gi">
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#5B5299]" data-id="element-741" data-mp="5jmrv8x3w5bt">
                First, the essentials
              </p>
              <h1 id="contact-title" className="mt-3 text-3xl font-bold tracking-tight text-[#041F60]" data-id="element-742" data-mp="mwfavkifrv5">
                Let’s get your details.
              </h1>
              <p className="mt-3 text-[15px] leading-6 text-stone-600" data-id="element-743" data-mp="9jn9f2fi4wn3">
                Your details are only used to set up your account.
              </p>
              <div className="mt-7 grid gap-5 sm:grid-cols-2" data-id="element-744" data-mp="jahx8b8ec4an">
                <Field id="first-name" label="First name" required showRequiredIndicator={false} value={contact.firstName} error={contactErrors.firstName} placeholder="Amelia" onChange={value => updateContact('firstName', value)} data-id="element-745" data-mp="xb2ev42kru2k" />
                <Field id="last-name" label="Last name" required showRequiredIndicator={false} value={contact.lastName} error={contactErrors.lastName} placeholder="Carter" onChange={value => updateContact('lastName', value)} data-id="element-746" data-mp="2kx5eg1xz2xzt" />
                <div className="sm:col-span-2" data-id="element-747" data-mp="2g43z5stuswfp">
                  <Field id="email" label="Email address" required showRequiredIndicator={false} type="email" value={contact.email} error={contactErrors.email} placeholder="you@company.com" onChange={value => updateContact('email', value)} data-id="element-748" data-mp="2184qoh2u0ua1" />
                </div>
                <div className="sm:col-span-2" data-id="element-749" data-mp="3rllvw8d0ovsq">
                  <Field id="phone" label="Phone number" required showRequiredIndicator={false} type="tel" value={contact.phone} error={contactErrors.phone} placeholder="07700 900 000" onChange={value => updateContact('phone', value)} data-id="element-750" data-mp="3rkan1u4psxur" />
                </div>
              </div>
              <button onClick={continueContact} className="mt-8 flex w-full items-center justify-center gap-2 rounded-lg bg-[#041F60] px-4 py-3.5 text-sm font-bold text-white transition hover:bg-[#031849] focus:outline-none focus:ring-2 focus:ring-[#4BE4FF] focus:ring-offset-2" data-id="element-751" data-mp="pjyosqrg91f8">
                Continue <ArrowRightIcon size={17} data-id="element-752" data-mp="lgsaqwy3i5y7" />
              </button>
            </section>}

          {step === 'company' && <section className="mt-9" aria-labelledby="company-title" data-id="element-753" data-mp="368kneg670p66">
              <button onClick={() => setStep('contact')} className="mb-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[#5B5299] hover:text-[#041F60] focus:outline-none focus:underline" data-id="element-754" data-mp="2xrahyb1feaob">
                <ArrowLeftIcon size={15} data-id="element-755" data-mp="37p850v7ju1fc" /> Your details
              </button>
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#5B5299]" data-id="element-756" data-mp="32s7yykap8yo8">
                Your business
              </p>
              <h1 id="company-title" className="mt-3 text-3xl font-bold tracking-tight text-[#041F60]" data-id="element-757" data-mp="2eyzkiwpjj4xl">
                Find your registered company.
              </h1>
              <p className="mt-3 text-[15px] leading-6 text-stone-600" data-id="element-758" data-mp="2k0ozuojsq4rh">
                Search Companies House to pre-fill your company record.
              </p>

              {!selectedCompany ? <div className="relative mt-7" data-id="element-759" data-mp="1rc16s6st2b3d">
                  <label htmlFor="company-search" className="mb-1.5 block text-sm font-semibold" data-id="element-760" data-mp="1rb88lu7wwtwu">
                    Company name or number
                  </label>
                  <div className="relative" data-id="element-761" data-mp="5d5ts0dal9l">
                    <SearchIcon className="absolute left-3.5 top-3.5 text-stone-400" size={19} data-id="element-762" data-mp="4y6dann807pd" />
                    <input id="company-search" value={companyQuery} onChange={event => setCompanyQuery(event.target.value)} placeholder="Start typing your company name" autoComplete="off" className="w-full rounded-lg border border-[#A6B6F8]/60 py-3 pl-11 pr-10 text-base outline-none transition focus:border-[#041F60] focus:ring-2 focus:ring-[#4BE4FF] sm:text-sm" data-id="element-763" data-mp="38f4y4zyhk04w" />
                    {searching && <LoaderCircleIcon className="absolute right-3.5 top-3.5 animate-spin text-[#5B5299]" size={18} aria-label="Searching" data-id="element-764" data-mp="11ne0s17egqvx" />}
                  </div>
                  {companyQuery.length > 0 && companyQuery.length < 2 && <p className="mt-2 text-xs text-stone-500" data-id="element-765" data-mp="y1h1o89klt0w">
                      Enter at least 2 characters to search.
                    </p>}
                  {visibleResults.length > 0 && !searching && <ul className="absolute z-10 mt-2 w-full overflow-hidden rounded-lg border border-[#A6B6F8]/60 bg-white shadow-lg" aria-label="Company search results" data-id="element-766" data-mp="o29slx9vckjl">
                      {visibleResults.map(company => <li key={company.number} data-id="element-767" data-mp="2dgoca3615h88">
                          <button onClick={() => {
                    setSelectedCompany(company);
                    setCompanyQuery(company.name);
                  }} className="w-full border-b border-[#A6B6F8]/25 px-4 py-3.5 text-left last:border-0 hover:bg-[#A6B6F8]/15 focus:bg-[#A6B6F8]/15 focus:outline-none" data-id="element-768" data-mp="29ge6orm5gstw">
                            <span className="block text-sm font-bold text-[#041F60]" data-id="element-769" data-mp="2bjpy7ctjajh7">
                              {company.name}{' '}
                              <span className="font-medium text-stone-500" data-id="element-770" data-mp="1xrrvpkubvlu0">
                                · {company.number}
                              </span>
                            </span>
                            <span className="mt-1 flex items-center gap-1 text-xs text-stone-500" data-id="element-771" data-mp="1nwq8wvniyvul">
                              <MapPinIcon size={12} data-id="element-772" data-mp="1su9mdtcef4lh" />
                              {company.address}
                            </span>
                            <span className="mt-1 inline-block rounded-full bg-[#4BE4FF]/20 px-2 py-0.5 text-[11px] font-semibold text-[#041F60] ring-1 ring-inset ring-[#4BE4FF]/60" data-id="element-773" data-mp="1jt1zs8j8vs78">
                              {company.status}
                            </span>
                          </button>
                        </li>)}
                    </ul>}
                  {companyQuery.length >= 2 && !searching && visibleResults.length === 0 && <p className="mt-3 text-sm text-stone-500" data-id="element-774" data-mp="1j5cesliskdja">
                        No matching companies found. Try a different name or
                        company number.
                      </p>}
                </div> : <CompanyConfirmation company={selectedCompany} onChange={() => {
              setSelectedCompany(null);
              setCompanyQuery('');
            }} data-id="element-775" data-mp="1nxclcwtda4ou" />}

              <button disabled={!selectedCompany} onClick={() => goToTerms('company')} className="mt-7 flex w-full items-center justify-center gap-2 rounded-lg bg-[#041F60] px-4 py-3.5 text-sm font-bold text-white transition enabled:hover:bg-[#031849] disabled:cursor-not-allowed disabled:bg-stone-300" data-id="element-776" data-mp="1wzlubi8zlq04">
                Continue to terms <ArrowRightIcon size={17} data-id="element-777" data-mp="18ckmioimsi8s" />
              </button>
              <button onClick={() => setStep('sole-trader')} className="mt-6 w-full text-sm font-semibold text-[#5B5299] underline decoration-[#A6B6F8] underline-offset-4 hover:text-[#041F60]" data-id="element-778" data-mp="1dbdu60b8lf11">
                I can&apos;t find my company / I&apos;m a sole trader
              </button>
            </section>}

          {step === 'sole-trader' && <section className="mt-9" aria-labelledby="sole-title" data-id="element-779" data-mp="3p16peq4eqs5j">
              <button onClick={() => setStep('company')} className="mb-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[#5B5299] hover:text-[#041F60]" data-id="element-780" data-mp="3t31lng7hefcg">
                <ArrowLeftIcon size={15} data-id="element-781" data-mp="3gq7osov0d52g" /> Registered company
              </button>
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#5B5299]" data-id="element-782" data-mp="3btplltfbtes8">
                Your business
              </p>
              <h1 id="sole-title" className="mt-3 text-3xl font-bold tracking-tight text-[#041F60]" data-id="element-783" data-mp="37ozsudfysgbj">
                Tell us about your work.
              </h1>
              <p className="mt-3 text-[15px] leading-6 text-stone-600" data-id="element-784" data-mp="3b1nrvxa2ylz">
                Sole traders do not appear in Companies House, so we’ll collect
                these details directly.
              </p>
              <div className="mt-7 space-y-5" data-id="element-785" data-mp="2k1i831v4aii2">
                <Field id="trading-name" label="Business or trading name" required value={soleBusiness} placeholder="e.g. Eleanor Smith Design" onChange={setSoleBusiness} data-id="element-786" data-mp="2k29pn8rvqfpc" />
                <Field id="trading-address" label="Business address" required value={soleAddress} placeholder="Street, town, postcode" onChange={setSoleAddress} data-id="element-787" data-mp="n1nfslr4lluq" />
              </div>
              <button disabled={!soleBusiness || !soleAddress} onClick={() => goToTerms('sole-trader')} className="mt-7 flex w-full items-center justify-center gap-2 rounded-lg bg-[#041F60] px-4 py-3.5 text-sm font-bold text-white transition enabled:hover:bg-[#031849] disabled:cursor-not-allowed disabled:bg-stone-300" data-id="element-788" data-mp="11r3660mqvp9w">
                Continue to terms <ArrowRightIcon size={17} data-id="element-789" data-mp="1kn1lthymjv01" />
              </button>
              <button onClick={() => setStep('company')} className="mt-6 w-full text-sm font-semibold text-[#5B5299] underline decoration-[#A6B6F8] underline-offset-4 hover:text-[#041F60]" data-id="element-790" data-mp="1pfmbq8eetnya">
                Registered company instead?
              </button>
            </section>}

          {step === 'terms' && <section className="mt-9" aria-labelledby="terms-title" data-id="element-791" data-mp="3ewgh82ieursv">
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#5B5299]" data-id="element-792" data-mp="2r6m4rm207595">
                Terms and conditions
              </p>
              <h1 id="terms-title" className="mt-3 text-3xl font-bold tracking-tight text-[#041F60]" data-id="element-793" data-mp="2m9dl1h17it5s">
                Review the terms.
              </h1>
              <p className="mt-3 text-[15px] leading-6 text-stone-600" data-id="element-794" data-mp="2v6xql263rbm7">
                Check the key terms below, download the full document and
                confirm you approve them.
              </p>
              <TermsStage accepted={acceptedTerms} showError={termsError} plan={selectedPlan} backLabel={termsBackStep === 'sole-trader' ? 'Your business' : 'Registered company'} onBack={() => setStep(termsBackStep)} onAcceptChange={next => {
              setAcceptedTerms(next);
              if (next) setTermsError(false);
            }} onSubmit={submitTerms} data-id="element-795" data-mp="ai08i3aqftld" />
            </section>}

          {step === 'complete' && <section className="mt-9 overflow-hidden rounded-2xl border border-[#A6B6F8]/50 bg-white" aria-labelledby="complete-title" data-id="element-796" data-mp="sy2zbjpnr9w0">
              <div className="navy-texture relative overflow-hidden border-b border-[#031849] px-6 py-10 text-white sm:px-9" data-id="element-797" data-mp="11y7yj6kp71z3">
                <Confetti pieceCount={44} data-id="element-798" data-mp="xxfnq8w6gp0p" />
                <motion.div initial={{
                scale: 0.5,
                opacity: 0
              }} animate={{
                scale: 1,
                opacity: 1
              }} transition={{
                type: 'spring',
                stiffness: 320,
                damping: 18
              }} className="relative grid h-12 w-12 place-items-center rounded-full bg-[#4BE4FF] text-[#041F60]" data-id="element-799" data-mp="xyha39in8xxq">
                  <PartyPopperIcon size={26} aria-hidden="true" data-id="element-800" data-mp="1citr8x386yqj" />
                </motion.div>
                <motion.p initial={{
                y: 10,
                opacity: 0
              }} animate={{
                y: 0,
                opacity: 1
              }} transition={{
                delay: 0.12,
                duration: 0.35
              }} className="relative mt-5 text-xs font-bold uppercase tracking-[0.16em] text-[#4BE4FF]" data-id="element-801" data-mp="xtxc6g2r4lgf">
                  Details received
                </motion.p>
                <motion.h1 id="complete-title" initial={{
                y: 10,
                opacity: 0
              }} animate={{
                y: 0,
                opacity: 1
              }} transition={{
                delay: 0.2,
                duration: 0.35
              }} className="relative mt-2 text-3xl font-bold tracking-tight" data-id="element-802" data-mp="sszu03hqrrex">
                  You’re all set — welcome to Credit Manager.
                </motion.h1>
              </div>
              <div className="p-6 sm:p-9" data-id="element-803" data-mp="2cr09k2u1zhvo">
                <p className="text-[15px] leading-7 text-stone-600" data-id="element-804" data-mp="2crru8im43v76">
                  You’ll receive an email from Satago to connect your accounting
                  software. Once your account is ready, you can safely connect
                  Xero, QuickBooks or Sage there.
                </p>
                {selectedPlan && <div className="mt-6 flex items-baseline justify-between gap-3 rounded-lg border border-[#A6B6F8]/50 bg-white p-4" data-id="element-805" data-mp="2vi18iqv6bdf8">
                    <div data-id="element-806" data-mp="2qhvbev64wypr">
                      <p className="text-xs font-bold uppercase tracking-[0.14em] text-[#5B5299]" data-id="element-807" data-mp="2qioki2skg0b2">
                        Your plan
                      </p>
                      <p className="mt-1 text-sm font-bold text-[#041F60]" data-id="element-808" data-mp="1f3i0ss0e6fro">
                        {selectedPlan.name}
                      </p>
                    </div>
                    <p className="text-right text-sm text-[#3A3A3A]" data-id="element-809" data-mp="19yxgralghjsc">
                      <span className="font-bold text-[#041F60]" data-id="element-810" data-mp="19z76qy6i3qy1">
                        {formatPrice(selectedPlan.price)}
                      </span>{' '}
                      / month after your trial
                      {selectedPlan.discountLabel && <span className="mt-0.5 block text-xs font-semibold text-[#5B5299]" data-id="element-811" data-mp="1npan58ip8qfk">
                          {selectedPlan.discountLabel}{' '}
                          {selectedPlan.discountPercent}% off applied
                        </span>}
                    </p>
                  </div>}
                <div className="mt-6 rounded-lg border border-[#A6B6F8]/50 bg-[#A6B6F8]/10 p-4" data-id="element-812" data-mp="22lii93vnbc9m">
                  <p className="text-sm font-bold text-[#041F60]" data-id="element-813" data-mp="3ck26osnx9lk">What happens next</p>
                  <ol className="mt-3 space-y-3 text-sm text-[#3A3A3A]" data-id="element-814" data-mp="8a3ombziksol">
                    <li className="flex gap-3" data-id="element-815" data-mp="1ypmi4s8wn6b2">
                      <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-[#4BE4FF] text-xs font-bold text-[#041F60]" data-id="element-816" data-mp="1ysifofgubsyz">
                        1
                      </span>
                      We’ll finish setting up your account.
                    </li>
                    <li className="flex gap-3" data-id="element-817" data-mp="22t2avdhusmih">
                      <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-[#4BE4FF] text-xs font-bold text-[#041F60]" data-id="element-818" data-mp="1f26qh1fq8tl9">
                        2
                      </span>
                      Satago sends your secure connection email.
                    </li>
                  </ol>
                </div>
                <p className="mt-6 flex items-center gap-2 text-sm text-stone-600" data-id="element-819" data-mp="1k0zrvvhqou52">
                  <LifeBuoyIcon size={17} className="shrink-0 text-[#5B5299]" data-id="element-820" data-mp="wbfavi55c6yf" />
                  Need help?{' '}
                  <a href="mailto:support@creditmanager.co.uk" className="font-semibold text-[#041F60] underline underline-offset-4" data-id="element-821" data-mp="rccesn759b2l">
                    support@creditmanager.co.uk
                  </a>
                </p>
              </div>
              </section>}
          </div>
        </main>
      </div>

      <SignupAside data-id="element-822" data-mp="2yy2qtmoovymf" />
    </div>;
}
function ProgressRail({
  step,
  includePlanStep
}: {
  step: JourneyStep;
  includePlanStep: boolean;
}) {
  const steps = [...(includePlanStep ? [{
    key: 'plan',
    label: 'Plan'
  }] : []), {
    key: 'contact',
    label: 'Your details'
  }, {
    key: 'company',
    label: 'Your business'
  }, {
    key: 'terms',
    label: 'Terms'
  }, {
    key: 'complete',
    label: 'Confirmation'
  }];
  const activeIndex = step === 'sole-trader' ? steps.findIndex(item => item.key === 'company') : steps.findIndex(item => item.key === step);
  return <nav aria-label="Onboarding progress" data-id="element-823" data-mp="2683xvg7djhch">
      <div className="flex items-baseline justify-between gap-3" data-id="element-824" data-mp="1m8bzdtfd88vn">
        <p className="text-sm font-bold text-[#041F60]" data-id="element-825" data-mp="1i6h0evkwq7l1">
          {steps[activeIndex]?.label}
        </p>
        <p className="text-xs font-semibold text-stone-500" data-id="element-826" data-mp="1dfs5t7dxkuho">
          Step {activeIndex + 1} of {steps.length}
        </p>
      </div>
      <ol className="mt-2.5 flex gap-1.5" data-id="element-827" data-mp="18fmextfhjc0n">
        {steps.map((item, index) => <li key={item.key} className="flex-1" data-id="element-828" data-mp="13hum8x70nlve" data-mpi={index}>
            <span className="sr-only" data-id="element-829" data-mp="13fhu5r7jnv4m">
              {item.label}
              {index < activeIndex ? ' — completed' : index === activeIndex ? ' — current step' : ''}
            </span>
            <span aria-hidden="true" className={`block h-1 rounded-full transition-colors duration-200 ${index <= activeIndex ? 'bg-[#041F60]' : 'bg-[#A6B6F8]/45'}`} data-id="element-830" data-mp="pil8032df1u2" />
          </li>)}
      </ol>
    </nav>;
}
function CompanyConfirmation({
  company,
  onChange
}: {
  company: Company;
  onChange: () => void;
}) {
  return <div className="mt-7 rounded-xl border border-[#A6B6F8]/60 bg-[#A6B6F8]/15 p-5" data-id="element-831" data-mp="2wdes5afnjo7z">
      <div className="flex items-start justify-between gap-3" data-id="element-832" data-mp="314wrju211gqu">
        <div className="flex gap-3" data-id="element-833" data-mp="317j11z8ha6bp">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-[#4BE4FF] text-[#041F60]" data-id="element-834" data-mp="364j74a5bv92t">
            <Building2Icon size={18} data-id="element-835" data-mp="28hj2ei4klpha" />
          </span>
          <div data-id="element-836" data-mp="2dfkm959tlstt">
            <p className="text-base font-bold" data-id="element-837" data-mp="2df2g9gxcw08p">{company.name}</p>
            <p className="mt-0.5 text-sm text-stone-600" data-id="element-838" data-mp="2dhopdkzwant1">
              Company no. {company.number} ·{' '}
              <span className="font-semibold text-emerald-700" data-id="element-839" data-mp="2ih1ket0x716p">
                {company.status}
              </span>
            </p>
          </div>
        </div>
        <button onClick={onChange} className="text-sm font-bold text-[#5B5299] underline underline-offset-4 hover:text-[#041F60]" data-id="element-840" data-mp="3tt2gkt045rwh">
          Change
        </button>
      </div>
      <dl className="mt-5 grid gap-4 border-t border-[#A6B6F8]/50 pt-4 text-sm sm:grid-cols-2" data-id="element-841" data-mp="ns3bzhj166c9">
        <div data-id="element-842" data-mp="iu1uh1196f2w">
          <dt className="text-xs font-semibold uppercase tracking-wide text-stone-500" data-id="element-843" data-mp="iubjomeh4i7j">
            Registered address
          </dt>
          <dd className="mt-1 leading-5" data-id="element-844" data-mp="sqy6vhu9vlcq">{company.address}</dd>
        </div>
        <div data-id="element-845" data-mp="sotsg5yqz3y7">
          <dt className="text-xs font-semibold uppercase tracking-wide text-stone-500" data-id="element-846" data-mp="16i2xjufnx3ax">
            Incorporated
          </dt>
          <dd className="mt-1" data-id="element-847" data-mp="11jrmjgdmsmr4">{company.incorporated}</dd>
          <dt className="mt-3 text-xs font-semibold uppercase tracking-wide text-stone-500" data-id="element-848" data-mp="11lchv87rs9q1">
            SIC codes
          </dt>
          <dd className="mt-1 leading-5" data-id="element-849" data-mp="1bgmns71ah193">{company.sicCodes.join(' · ')}</dd>
        </div>
      </dl>
    </div>;
}